function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { MutableRefObject, FC, RefObject, CSSProperties, ElementType, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import cn from 'classnames';
import { ArrowButton } from '../../_internal_/components/ArrowButton';
require("./HorizontalScroll.css");
var styles = {
  "Root": "HorizontalScroll-module-Root_U-1",
  "Container": "HorizontalScroll-module-Container_1Gw",
  "Wrap": "HorizontalScroll-module-Wrap_2y8",
  "ItemsWrap": "HorizontalScroll-module-ItemsWrap_jFk",
  "hasArrows": "HorizontalScroll-module-hasArrows_31Q",
  "arrowLine": "HorizontalScroll-module-arrowLine_36V",
  "scrollDirection_both": "HorizontalScroll-module-scrollDirection_both_144",
  "scrollDirection_right": "HorizontalScroll-module-scrollDirection_right_2M1",
  "scrollDirection_left": "HorizontalScroll-module-scrollDirection_left_3eI",
  "widthFull": "HorizontalScroll-module-widthFull_29z",
  "sameHeight": "HorizontalScroll-module-sameHeight_21a",
  "Item": "HorizontalScroll-module-Item_A-A",
  "noGap": "HorizontalScroll-module-noGap_7qm"
};
const cnHorizontalScrollContainer = styles.Container;
const cnHorizontalScrollArrow = styles.Arrow;
const cnHorizontalScrollWrap = styles.Wrap;
const cnHorizontalScrollItemsWrap = styles.ItemsWrap;
function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
function scroll(element, offset, duration = 300) {
  let start = 0;
  const scrollStart = element.scrollLeft;
  requestAnimationFrame(function animate(timestamp) {
    if (start === 0) {
      start = timestamp;
    }
    let timeFraction = (timestamp - start) / duration;
    if (timeFraction > 1) {
      timeFraction = 1;
    }
    const diff = Math.ceil(offset * easeInOutQuad(timeFraction));
    element.scrollLeft = scrollStart + diff;
    if (timeFraction < 1) {
      requestAnimationFrame(animate);
    }
  });
}

/**
 * Компонент скроллера.
 */
export const HorizontalScroll = ({
  className,
  innerRef,
  controlRef,
  style = {},
  tag = 'div',
  afterMountTimerId,
  onScrollRightEnd,
  endEventOffset,
  onScrollLeftEnd,
  onClickArrow,
  scrollStep = 100,
  rightPointScrollToEnd,
  leftPointScrollToStart,
  onScroll,
  sameHeight,
  hideArrowLine,
  children,
  arrowsAlignHeight,
  noGap,
  hideArrow,
  ...props
}) => {
  const [scrollDirection, setScrollDirection] = useState('none');
  const [skipScrollCtr, setSkipScrollCtr] = useState(false);
  const [itemsLength, setItemsLength] = useState(0);
  const Component = tag;
  const items = [];
  const wrapRef = useRef(null);
  const step = useRef(scrollStep);
  const setScrollMod = domElem => {
    // Нужно округлить при масштабировании окна, т.к. scrollLeft будет дробный и чуть меньше ширины скролла
    const left = Math.ceil(domElem.scrollLeft);
    const hiddenWidth = Math.ceil(domElem.scrollWidth - domElem.clientWidth);
    let scrollDirection = 'both';
    if (hiddenWidth <= 0) {
      scrollDirection = 'none';
    } else if (left <= 0) {
      scrollDirection = 'right';
    } else if (left >= hiddenWidth) {
      scrollDirection = 'left';
    }
    setScrollDirection(scrollDirection);
  };

  /**
   * Установка флага необходимости отправки счетчика скролла,
   * который должен отправиться при скролле мышкой/тачпадом и не отправиться при клике по стрелкам
   */
  const skipOnscrollCounter = () => {
    setSkipScrollCtr(true);
  };

  /**
   * Скроллит контейнер на `offset` пикселей от текущего смещения
   *
   * @param offset - величина смещения от текущего значения
   * @param disableAnimation - отключает плавную прокрутку
   */
  const scrollBy = useCallback((offset, disableAnimation) => {
    const domElem = wrapRef.current;
    if (!domElem) return;
    skipOnscrollCounter();
    if (disableAnimation) {
      domElem.scrollLeft += offset;
    } else {
      scroll(domElem, offset);
    }
    setScrollMod(domElem);
  }, []);
  const reset = useCallback(() => {
    if (!wrapRef.current) return;
    setScrollMod(wrapRef.current);
  }, []);
  const scrollLeft = useCallback(() => {
    const domElem = wrapRef.current;
    skipOnscrollCounter();
    if (domElem) {
      if (leftPointScrollToStart) {
        // Нужно округление при масштабировании окна
        const scrollLeft = Math.round(domElem.scrollLeft);
        let offset = scrollLeft - step.current;
        if (offset <= leftPointScrollToStart) {
          offset = 0;
        }
        step.current = scrollLeft - offset;
      }
      scrollBy(-step.current);
      if (onClickArrow) {
        onClickArrow('left');
      }
    }
  }, [leftPointScrollToStart, onClickArrow, scrollBy]);
  const scrollRight = useCallback(() => {
    const domElem = wrapRef.current;
    skipOnscrollCounter();
    if (domElem) {
      if (rightPointScrollToEnd) {
        // Нужно округление при масштабировании окна
        const wrapWidth = Math.round(domElem.offsetWidth);
        const scrollLeft = Math.round(domElem.scrollLeft);
        const offset = scrollLeft + step.current;

        // Проверяем попадет ли позиция rightPointScrollToEnd в видимую область
        if (offset + wrapWidth >= rightPointScrollToEnd && wrapWidth < rightPointScrollToEnd) {
          // Рассчитываем на сколько нужно увеличить scrollStep чтобы доскроллить до конца вправо
          step.current = domElem.scrollWidth - wrapWidth - scrollLeft;
        }
      }
      scrollBy(step.current);
      if (onClickArrow) {
        onClickArrow('right');
      }
    }
  }, [onClickArrow, rightPointScrollToEnd, scrollBy]);
  const sendCounter = useCallback(() => {
    if (skipScrollCtr) {
      setSkipScrollCtr(false);
      return;
    }
    if (onScroll) onScroll();
  }, [skipScrollCtr, onScroll]);
  const getWrapScrollLeft = domElem => {
    return Math.ceil(domElem.scrollLeft);
  };
  const getHiddenWidth = domElem => {
    const rect = domElem.getBoundingClientRect();
    return Math.ceil(domElem.scrollWidth - rect.width);
  };
  const checkEndOfList = useCallback(domElem => {
    if (!endEventOffset || !onScrollLeftEnd && !onScrollRightEnd) {
      return;
    }
    const left = getWrapScrollLeft(domElem);
    const hidden = getHiddenWidth(domElem);
    if (left <= endEventOffset && onScrollLeftEnd) {
      onScrollLeftEnd();
    }
    if (left > hidden - endEventOffset && onScrollRightEnd) {
      onScrollRightEnd();
    }
  }, [endEventOffset, onScrollLeftEnd, onScrollRightEnd]);
  const onHandleScroll = useCallback(({
    currentTarget
  }) => {
    sendCounter();
    checkEndOfList(currentTarget);
    setScrollMod(currentTarget);
  }, [checkEndOfList, sendCounter]);
  const hasArrows = !hideArrow;
  const arrowLine = !hideArrowLine;
  const horizontalScrollMods = cn(styles[`scrollDirection_${scrollDirection}`], {
    [styles.hasArrows]: hasArrows,
    [styles.widthFull]: !hasArrows,
    [styles.noGap]: noGap,
    [styles.sameHeight]: sameHeight,
    [styles.arrowLine]: arrowLine
  });
  useEffect(() => {
    window.addEventListener('resize', reset);
    innerRef && (innerRef.current = wrapRef.current);
    sendCounter();
    const afterMountTimerId = setTimeout(() => {
      wrapRef.current && setScrollMod(wrapRef.current);
    });
    return () => {
      window.removeEventListener('resize', reset);
      innerRef && (innerRef.current = null);
      clearTimeout(afterMountTimerId);
    };
  }, [innerRef, reset, sendCounter]);
  useEffect(() => {
    if (itemsLength !== items.length) {
      // Перерисовываем скроллер, т.к. после добавления элементов — стрелки не перерисовываются.
      reset();
      setItemsLength(items.length);
    }
  }, [itemsLength, items.length, reset]);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cn(styles.Root, horizontalScrollMods, className),
    style: style,
    ref: innerRef || controlRef
  }, props), /*#__PURE__*/React.createElement("div", {
    className: cnHorizontalScrollContainer
  }, hasArrows && /*#__PURE__*/React.createElement(ArrowButton, {
    className: cnHorizontalScrollArrow,
    direction: "left",
    visible: scrollDirection === 'left' || scrollDirection === 'both',
    arrowsAlignHeight: arrowsAlignHeight,
    onClick: scrollLeft
  }), /*#__PURE__*/React.createElement("div", {
    className: cnHorizontalScrollWrap,
    onScroll: onHandleScroll,
    ref: wrapRef
  }, sameHeight ? /*#__PURE__*/React.createElement("div", {
    className: cnHorizontalScrollItemsWrap
  }, children) : children), hasArrows && /*#__PURE__*/React.createElement(ArrowButton, {
    className: cnHorizontalScrollArrow,
    direction: "right",
    visible: scrollDirection === 'right' || scrollDirection === 'both',
    arrowsAlignHeight: arrowsAlignHeight,
    onClick: scrollRight
  })));
};
if (process.env.NODE_ENV !== 'production') {
  HorizontalScroll.displayName = 'HorizontalScroll';
}